import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../data/translations';
import axios from 'axios';
import { LogIn, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const { lang } = useLanguage();
  const t = translations.admin[lang];
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (data.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        setError('Admin access required');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-enbi-navy flex items-center justify-center px-6">
      {/* Diagonal gold accent */}
      <div
        className="absolute top-0 right-0 w-[40%] h-full bg-enbi-gold/[0.05]"
        style={{ clipPath: 'polygon(40% 0, 100% 0, 100% 100%, 0 100%)' }}
      />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-10">
          <img src="/logo-monogram-white.png" alt="ENBI" className="w-16 h-16 mx-auto mb-4" />
          <h1 className="font-outfit text-2xl text-white font-bold tracking-wider uppercase">{t.login}</h1>
          <div className="w-10 h-[2px] bg-enbi-gold mx-auto mt-4" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            placeholder={t.email}
            data-testid="admin-email-input"
            className="w-full bg-white/[0.06] border border-white/15 px-5 py-4 text-white text-sm placeholder:text-white/35 focus:border-enbi-gold focus:outline-none transition-colors"
          />
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            placeholder={t.password}
            data-testid="admin-password-input"
            className="w-full bg-white/[0.06] border border-white/15 px-5 py-4 text-white text-sm placeholder:text-white/35 focus:border-enbi-gold focus:outline-none transition-colors"
          />
          {error && <p className="text-red-400 text-sm" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-enbi-gold text-enbi-navy px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold hover:bg-enbi-gold-light transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-3"
          >
            <LogIn size={16} strokeWidth={1.5} />
            {loading ? '...' : t.submit}
          </button>
        </form>

        <button
          onClick={() => navigate('/')}
          className="mt-8 flex items-center gap-2 text-white/40 text-xs tracking-wider uppercase hover:text-enbi-gold transition-colors mx-auto"
          data-testid="admin-back-to-site"
        >
          <ArrowLeft size={14} />
          {t.back}
        </button>
      </div>
    </div>
  );
}
